import {
  Box,
  Button,
  Flex,
  Icon,
  Slide,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';
import { IoListOutline } from '@react-icons/all-files/io5/IoListOutline';
import { IoChevronUp } from '@react-icons/all-files/io5/IoChevronUp';
import { useWindowScroll, useMeasure } from 'react-use';
import { FC, MutableRefObject, useEffect, useMemo, useRef } from 'react';
import { TableOfContents } from './TableOfContents';
import { TableOfContentLinks } from '../../hooks/useTableOfContentLinks';
import { useLockBodyScroll } from 'react-use';

interface StickyArticleButtonsProps {
  links: TableOfContentLinks;
  tocRef: MutableRefObject<HTMLDivElement | null>;
}

export const StickyArticleButtons: FC<StickyArticleButtonsProps> = ({
  links,
  tocRef: parentTocRef,
}) => {
  const { x, y } = useWindowScroll();
  const { isOpen, onToggle, onOpen, onClose } = useDisclosure();
  const tocRef = useRef(null);
  useOutsideClick({
    ref: tocRef,
    handler: () => {
      onClose();
    },
  });

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const renderStickyButtons = useMemo(() => {
    if (parentTocRef.current) {
      const bounds = parentTocRef.current.getBoundingClientRect();

      return y > parentTocRef.current.offsetTop + bounds.height;
    }

    return false;
  }, [parentTocRef, y]);
  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (!renderStickyButtons) {
      onClose();
    }
  }, [renderStickyButtons]);

  return (
    <Box
      display={{
        base: 'block',
        md: 'none',
      }}
    >
      {isOpen && (
        <Box
          pos="fixed"
          bottom={0}
          left={0}
          right={0}
          top={0}
          bg="rgba(255,255,255, .8)"
          zIndex={14}
        />
      )}

      <Slide direction="bottom" in={renderStickyButtons} style={{ zIndex: 15 }}>
        <Box p={2} ref={tocRef}>
          {isOpen && (
            <TableOfContents
              mb={4}
              bg="white"
              links={links}
              onClick={onClose}
              showClose
              onClose={onClose}
              isSticky
            />
          )}

          <Flex justifyContent="space-between">
            <Button variant="roundBlue" size="sm" px={4} onClick={onToggle}>
              <Icon as={IoListOutline} mr={2} fontSize="xl" />
              Inhaltsverzeichnis
            </Button>
            <Button
              variant="roundBlue"
              size="sm"
              pl={4}
              pr={2}
              onClick={scrollToTop}
            >
              Nach oben
              <Icon as={IoChevronUp} ml={1} fontSize="xl" />
            </Button>
          </Flex>
        </Box>
      </Slide>
    </Box>
  );
};
